import { JsonApiRequestError, Mutation, Query } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { ComboboxData } from '@mantine/core';
import { createStore, sample, Store, StoreWritable } from 'effector';
import { or, spread } from 'patronum';
import {
	ACCOUNT_NAME_MIN_LENGTH,
	ACCOUNT_NOTE_MAX_LENGTH,
	accountTreeModel,
} from 'entities/account';
import { viewerModel } from 'entities/viewer';
import {
	AccountWithWalletsAndPermissions,
	AddAccountMutationParams,
	AddAccountResponse,
	createAddAccountMutation,
	createGetAccountQuery,
	createGetAccountsQuery,
	EntityWithName,
} from 'shared/api';
import {
	createModalFormModel,
	maxLengthRule,
	minLengthRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type AddAccountFormValue = {
	name: string;
	parentId: number | null;
	currencies: string[];
	note: string;
	canCreateSubAccounts: boolean;
	canWriteUsers: boolean;
	canEditLimits: boolean;
	canEditGameSettings: boolean;
	canWriteCashdesks: boolean;
	canBlockGames: boolean;
};

export type AddAccountModel = ModalFormModel<null, AddAccountFormValue> & {
	addAccountMutation: Mutation<AddAccountMutationParams, AddAccountResponse, JsonApiRequestError>;
	getParentAccountQuery: Query<number, AccountWithWalletsAndPermissions, JsonApiRequestError>;
	getAccountsQuery: Query<void, EntityWithName[], JsonApiRequestError>;
	$accounts: StoreWritable<ComboboxData>;
	$pending: Store<boolean>;
	$currencies: Store<string[]>;
};

export const createAddAccountModel = (): AddAccountModel => {
	const getAccountsQuery = createGetAccountsQuery();

	const getParentAccountQuery = createGetAccountQuery();

	const addAccountMutation = createAddAccountMutation();

	const model = createModalFormModel<null, AddAccountFormValue>({
		fields: {
			name: {
				init: '',
				rules: [notEmptyRule, minLengthRule(ACCOUNT_NAME_MIN_LENGTH)],
			},
			parentId: {
				init: null,
				rules: [notEmptyRule],
			},
			currencies: {
				init: [],
			},
			note: {
				init: '',
				rules: [maxLengthRule(ACCOUNT_NOTE_MAX_LENGTH)],
			},
			canCreateSubAccounts: {
				init: false,
			},
			canWriteUsers: {
				init: false,
			},
			canEditLimits: {
				init: false,
			},
			canEditGameSettings: {
				init: false,
			},
			canWriteCashdesks: {
				init: false,
			},
			canBlockGames: {
				init: false,
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	const $accounts = createStore<ComboboxData>([]);

	const $currencies = getParentAccountQuery.$data.map((data) =>
		data ? data.wallets.filter(({ isLocked }) => !isLocked).map((wallet) => wallet.currency) : [],
	);

	const $pending = or(
		getAccountsQuery.$pending,
		getParentAccountQuery.$pending,
		addAccountMutation.$pending,
	);

	sample({
		clock: model.modalButtonClicked,
		target: [model.form.fields.currencies.disable, getAccountsQuery.start],
	});

	sample({
		clock: viewerModel.$permissions,
		filter: Boolean,
		target: spread({
			canCreateSubAccounts: model.form.fields.canCreateSubAccounts.setActive,
			canBlockGames: model.form.fields.canBlockGames.setActive,
			canWriteCashdesks: model.form.fields.canWriteCashdesks.setActive,
			canWriteUsers: model.form.fields.canWriteUsers.setActive,
			canEditLimits: model.form.fields.canEditLimits.setActive,
			canEditGameSettings: model.form.fields.canEditGameSettings.setActive,
		}),
	});

	sample({
		clock: getAccountsQuery.finished.success,
		fn: ({ result }) => result.map((account) => ({ value: `${account.id}`, label: account.name })),
		target: $accounts,
	});

	sample({
		clock: model.form.fields.parentId.$value,
		target: [model.form.fields.currencies.reset, model.form.fields.currencies.disable],
	});

	sample({
		clock: model.form.fields.parentId.$value,
		filter: Boolean,
		target: getParentAccountQuery.start,
	});

	chain(getParentAccountQuery.finished.success, model.form.fields.currencies.enable);

	sample({
		clock: model.form.submitted,
		source: model.form.$values,
		fn: ({ name, note, parentId, currencies, ...permissions }) => ({
			name,
			note,
			parentId: parentId!,
			currencies,
			permissions,
		}),
		target: addAccountMutation.start,
	});

	sample({
		clock: addAccountMutation.finished.success,
		target: [model.switcher.disable, accountTreeModel.fetch],
	});

	notify(addAccountMutation.finished.success, 'add-account.success');

	sample({
		clock: model.modalClosed,
		target: [getAccountsQuery.reset, getParentAccountQuery.reset, addAccountMutation.reset],
	});

	return {
		addAccountMutation,
		getParentAccountQuery,
		getAccountsQuery,
		$accounts,
		$pending,
		$currencies,
		...model,
	};
};
